import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { NavController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService } from "src/app/_services/localStorage.service";
import { StripeService } from "src/app/_services/stripe.service";
import { Cart } from "src/app/models/cart.model";

@Component({
  selector: "app-on-road-modal",
  templateUrl: "./on-road-modal.component.html",
  styleUrls: ["./on-road-modal.component.scss"],
  standalone: false,
})
export class OnRoadModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("animatedImage") animatedImage!: ElementRef;
  @ViewChild("bgImage") bgImage!: ElementRef;
  showModal: boolean = false;
  price: number = 0;
  currentState: string = "";
  cart!: Cart;
  role: any = "";
  @Input() titre: string = "";
  @Input() lable: string = "";
  @Input() attent: string = "";
  @Input() statut: string = "";
  @Input() buttonText: string = "";
  @Input() distance: { Km: number; M: number } = { Km: 0, M: 0 };
  @Input() pointsDistance: { Km: number; M: number; } = { Km: 999, M: 999, };
  @Input() hours: string = "";
  @Input() minutes: string = "";
  @Output() buttonClick: EventEmitter<any> = new EventEmitter<any>();
  @Input() startCoords: { lat: number; lng: number } | null = null;
  @Input() destinationCoords: { lat: number; lng: number } | null = null;
  @Input() confirm: boolean = false;
  @Input() loading: boolean = false;
  @Input() confimTitle: string = ``;
  @Input() confirmMessage: string = this.translate.instant("ONROAD.MESSAGE");
  @Input() proposed_provider: any;
  @Input() inRouteState: number = 0;
  @Input() job: string = "";
  @Input() layoutVersion = 0;
  animationTimer: any;
  testAnimationInterval: any;
  trajetLoadTimedOut = false;
  private trajetWaitTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly TRAJET_WAIT_TIMEOUT_MS = 9000;
  private lastInRouteState: number = 0;
  /** Matches trajet.svg viewBox (340) road endpoints. */
  private readonly TRAJET_TRACK_START = 36 / 340;
  private readonly TRAJET_TRACK_END = 304 / 340;

  constructor(
    private localStorageService: LocalStorageService,
    private translate: TranslateService,
    private stripeService: StripeService,
    private navController: NavController
  ) {
  }

  greaterThen(val1: number, val2: number): boolean {
    const greater = Math.floor(val1) > Math.floor(val2);
    return greater;
  }
  ngOnDestroy(): void {
    this.clearTrajetWaitTimeout();
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }
    if (this.restartAnimationTimeout) {
      clearTimeout(this.restartAnimationTimeout);
      this.restartAnimationTimeout = null;
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes["inRouteState"]) {
      const curr = Number(changes["inRouteState"].currentValue ?? 0);
      this.lastInRouteState = curr;
      this.startAnimation();
    }
    if (changes["layoutVersion"] && !changes["layoutVersion"].firstChange) {
      this.restartAnimation();
    }
    if (
      changes["distance"] ||
      changes["hours"] ||
      changes["minutes"] ||
      changes["statut"] ||
      changes["attent"]
    ) {
      if (this.hasTrajetDisplay()) {
        this.trajetLoadTimedOut = false;
        this.clearTrajetWaitTimeout();
        this.restartAnimation();
      } else {
        this.scheduleTrajetWaitTimeout();
      }
    }
  }

  private clearTrajetWaitTimeout(): void {
    if (this.trajetWaitTimer) {
      clearTimeout(this.trajetWaitTimer);
      this.trajetWaitTimer = null;
    }
  }

  private scheduleTrajetWaitTimeout(): void {
    this.clearTrajetWaitTimeout();
    const statut = String(this.statut ?? "").trim();
    if (!this.shouldRenderPanel()) {
      return;
    }
    if (
      statut === "PENDING" ||
      statut === "WAITING" ||
      statut === "PRE_ASSIGN"
    ) {
      return;
    }
    if (this.hasTrajetDisplay()) {
      this.trajetLoadTimedOut = false;
      return;
    }
    this.trajetWaitTimer = setTimeout(() => {
      this.trajetWaitTimer = null;
      if (!this.hasTrajetDisplay()) {
        this.trajetLoadTimedOut = true;
      }
    }, this.TRAJET_WAIT_TIMEOUT_MS);
  }

  isTransporterJob(): boolean {
    return this.job === "Transporter" || this.job === "Transporteur";
  }

  /** INPROGRESS/ONROAD must show even when `attent` is empty (API legacy). */
  shouldRenderPanel(): boolean {
    const statut = String(this.statut ?? "").trim();
    const attent = String(this.attent ?? "").trim();
    if (!statut) {
      return false;
    }
    if (
      statut === "INPROGRESS" ||
      statut === "ONROAD" ||
      statut === "PENDING" ||
      statut === "WAITING" ||
      statut === "PRE_ASSIGN"
    ) {
      return true;
    }
    return !!(statut && attent);
  }

  hasTrajetDisplay(): boolean {
    if (
      this.distance &&
      (Number(this.distance.Km) > 0 || Number(this.distance.M) > 0)
    ) {
      return true;
    }
    const hours = Number(this.hours);
    const minutes = Number(this.minutes);
    return (
      Number.isFinite(hours) &&
      Number.isFinite(minutes) &&
      (hours > 0 || minutes > 0)
    );
  }

  hasValidDestinationCoords(): boolean {
    if (!this.destinationCoords) {
      return false;
    }
    const lat = Number(this.destinationCoords.lat);
    const lng = Number(this.destinationCoords.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return false;
    }
    return !(lat === 0 && lng === 0);
  }

  private restartAnimationTimeout: any = null;
  restartAnimation(): void {
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }
    if (this.restartAnimationTimeout) {
      clearTimeout(this.restartAnimationTimeout);
    }
    this.restartAnimationTimeout = setTimeout(() => {
      this.restartAnimationTimeout = null;
      this.startAnimation();
    }, 50);
  }

  ngOnInit() {
    this.role = this.localStorageService.getUserRole();
    this.getConfigs();
    this.getCards();
    this.scheduleTrajetWaitTimeout();
  }

  ngAfterViewInit() {
    this.startAnimation();
  }

  private applyCarTransform(): boolean {
    if (!this.bgImage?.nativeElement || !this.animatedImage?.nativeElement) {
      return false;
    }
    const bgWidth = this.bgImage.nativeElement.offsetWidth;
    const carWidth = this.animatedImage.nativeElement.offsetWidth;
    const progress = Math.max(0, Math.min(100, Number(this.inRouteState) || 0));
    const trackStartPx = bgWidth * this.TRAJET_TRACK_START;
    const trackEndPx = bgWidth * this.TRAJET_TRACK_END - carWidth;
    const trackSpan = Math.max(0, trackEndPx - trackStartPx);
    const el = this.animatedImage.nativeElement;
    el.classList.remove("move-left", "move-right");
    el.style.animation = "none";
    if (this.role === "client") {
      const offset = (progress / 100) * trackSpan;
      el.style.left = "auto";
      el.style.right = `${Math.max(0, bgWidth - trackEndPx - carWidth)}px`;
      el.style.transform = `translateX(-${offset}px)`;
    } else {
      const offset = (progress / 100) * trackSpan;
      el.style.left = `${trackStartPx}px`;
      el.style.right = "auto";
      el.style.transform = `translateX(${offset}px)`;
    }
    return true;
  }

  startAnimation() {
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }

    this.applyCarTransform();

    if (this.inRouteState >= 100) {
      return;
    }

    let missingElementsTicks = 0;
    this.animationTimer = setInterval(() => {
      if (this.applyCarTransform()) {
        missingElementsTicks = 0;
        return;
      }
      missingElementsTicks++;
      if (missingElementsTicks >= 30) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }
    }, 100);
  }
  goToEditCart() {
    this.navController.navigateForward([
      "/particulier/configurations/payment-methods",
    ]);
  }

  getConfigs() {
    let configs = this.localStorageService.getConfigs();
    this.price = configs?.amount_for_cancelation
      ? configs?.amount_for_cancelation
      : 0;
  }
  getCards() {
    this.stripeService.getCard().subscribe((res: any) => {
      this.cart = res.find((item: any) => item.default === 1);
    });
  }

  click() {
    this.buttonClick.emit();
  }
  formateDistance(km: number, m: number): string {
    let distance = km ? `${km}.${m} Km` : m ? `${m} M` : "0 M";
    distance = km ? distance.slice(0, 4) + " Km" : distance;
    return distance;
  }
  openGoogleMaps() {
    if (!this.hasValidDestinationCoords() || !this.destinationCoords) {
      return;
    }
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${this.destinationCoords.lat},${this.destinationCoords.lng}&travelmode=driving`;
    window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
  }
  convertToMilliseconds(hours: number, minutes: number): number {
    return hours * 3600000 + minutes * 60000;
  }
}
